import { Close, Save, PhotoCamera, Delete } from "@mui/icons-material";
import { Avatar, Box, Button, CircularProgress, Dialog, IconButton, MenuItem, Skeleton, TextField, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import theme from "../../theme/theme";
import { uploadEmployeePhoto, removeEmployeePhoto } from "../../api/EmployeeService";
export const EditEmployeeDialog = ({ openEditDialog, setOpenEditDialog, fetchingEmployee, selectedEmployee, setSelectedEmployee, updateEmployee, errors, updatingEmployee, onPhotoUpdated }) => {
    const fileInputRef = useRef(null);
    const [photoBusy, setPhotoBusy] = useState(false);

    const handlePhotoFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file || !selectedEmployee?.id) return;
        setPhotoBusy(true);
        try {
            const res = await uploadEmployeePhoto(selectedEmployee.id, file);
            const photo = res?.data?.photo;
            setSelectedEmployee({ ...selectedEmployee, photo });
            toast.success("Photo updated");
            onPhotoUpdated?.();
        } catch (error) {
            toast.error(error?.data?.message || error?.message || "Failed to upload photo");
        } finally {
            setPhotoBusy(false);
        }
    };

    const handlePhotoRemove = async () => {
        if (!selectedEmployee?.id) return;
        setPhotoBusy(true);
        try {
            await removeEmployeePhoto(selectedEmployee.id);
            setSelectedEmployee({ ...selectedEmployee, photo: null });
            toast.success("Photo removed");
            onPhotoUpdated?.();
        } catch (error) {
            toast.error(error?.data?.message || error?.message || "Failed to remove photo");
        } finally {
            setPhotoBusy(false);
        }
    };

    const designations = [
        {
            value: 'sales',
            label: 'Sales'
        },
        { value: 'service', label: 'Service' },
        { value: 'engineer', label: 'Engineer' }, { value: 'sub_admin', label: 'Sub Admin' }, { value: 'others', label: 'Others' }
    ];
    return (
        <Dialog
            open={openEditDialog}
            onClose={() => setOpenEditDialog(false)}
            PaperProps={{
                sx: {
                    borderRadius: theme.dialog.borderRadius,
                    minWidth: theme.dialog.minWidth
                }
            }}>

            <Box sx={{ backgroundColor: theme.dialog.headerBgColor, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h6" fontWeight={'bold'} color={theme.palette.primary.main}>
                        Edit Employee
                    </Typography>
                </Box>
                <Box>
                    <IconButton onClick={() => setOpenEditDialog(false)}>
                        <Close />
                    </IconButton>
                </Box>
            </Box>
            <Box sx={{ p: 4 }}>
                {fetchingEmployee ? (
                    <>
                        <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
                        <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
                        <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
                        <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
                    </>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                            <Avatar
                                src={selectedEmployee.photo || undefined}
                                sx={{ width: 64, height: 64, fontSize: 24, backgroundColor: theme.palette.primary.main }}
                            >
                                {(selectedEmployee.name || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={photoBusy ? <CircularProgress size={14} /> : <PhotoCamera />}
                                    disabled={photoBusy}
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{ borderRadius: theme.button.borderRadius, textTransform: 'none' }}
                                >
                                    {selectedEmployee.photo ? 'Change Photo' : 'Upload Photo'}
                                </Button>
                                {selectedEmployee.photo && (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        startIcon={<Delete />}
                                        disabled={photoBusy}
                                        onClick={handlePhotoRemove}
                                        sx={{ borderRadius: theme.button.borderRadius, textTransform: 'none' }}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </Box>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                hidden
                                onChange={handlePhotoFile}
                            />
                        </Box>
                        <TextField
                            label="Name"
                            id="name"
                            fullWidth
                            value={selectedEmployee.name}
                            onChange={(e) => setSelectedEmployee({ ...selectedEmployee, name: e.target.value })}
                            error={!!errors.name}
                            helperText={errors.name}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="Employee ID"
                            id="employee_id"
                            fullWidth
                            value={selectedEmployee.employee_id || ''}
                            onChange={(e) => setSelectedEmployee({ ...selectedEmployee, employee_id: e.target.value })}
                            error={!!errors.employee_id}
                            helperText={errors.employee_id}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="Email"
                            id="email"
                            fullWidth
                            value={selectedEmployee.email}
                            onChange={(e) => setSelectedEmployee({ ...selectedEmployee, email: e.target.value })}
                            error={!!errors.email}
                            helperText={errors.email}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="Designation"
                            id="designation"
                            fullWidth
                            select
                            value={selectedEmployee.designation}
                            onChange={(e) => setSelectedEmployee({ ...selectedEmployee, designation: e.target.value })}
                            error={!!errors.designation}
                            helperText={errors.designation}
                            sx={{ mb: 2 }}
                        >
                            {designations.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Phone"
                            id="phone"
                            fullWidth
                            value={selectedEmployee.phone}
                            onChange={(e) => setSelectedEmployee({ ...selectedEmployee, phone: e.target.value })}
                            error={!!errors.phone}
                            helperText={errors.phone}
                            sx={{ mb: 2 }}
                        />
                    </>
                )}
            </Box>
            <Box sx={{ backgroundColor: theme.dialog.headerBgColor, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button variant="contained" onClick={() => setOpenEditDialog(false)} sx={{ backgroundColor: theme.button.backgroundColor, color: '#000', borderRadius: theme.button.borderRadius }}>
                    Close
                </Button>
                <Button variant="contained" onClick={() => updateEmployee()} sx={{ backgroundColor: theme.button.editBackgroundColor, borderRadius: theme.button.borderRadius }} loading={updatingEmployee} startIcon={<Save />}>
                    Update Employee
                </Button>
            </Box>
        </Dialog>
    );
}